import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, generatePath } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faListOl } from '@fortawesome/pro-duotone-svg-icons/faListOl';
import { faHandshake } from '@fortawesome/pro-duotone-svg-icons/faHandshake';
import { faEnvelopeCircleCheck } from '@fortawesome/pro-duotone-svg-icons/faEnvelopeCircleCheck';
import Cookies from 'universal-cookie';

import { colors } from 'us-react-components/dist/constants/Styles';
import { Button, ButtonSize, ButtonType } from '../../components';
import { Storage } from '../../constants/Storage';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';
import { Routes } from '../../Routes';
import AgencyCare from './agency-care.png';

export const InformationalScreen = (): JSX.Element => {
  const cookies = new Cookies();
  const { stores } = useDependencies();
  const { verification } = stores;
  const modal = useModal();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const iconStyle = {
    '--fa-primary-color': colors.emerald[700],
    '--fa-secondary-color': colors.emerald[300],
    '--fa-secondary-opacity': 1,
  } as CSSProperties;

  const circleStyle = {
    backgroundColor: 'rgba(145, 192, 241, 0.2)', // #91C0F1 with 20% opacity
  };

  const steps = [
    { icon: faListOl, id: 'icon1', title: t('agency.iconTitle1'), description: t('agency.iconDescription1') },
    { icon: faHandshake, id: 'icon2', title: t('agency.iconTitle2'), description: t('agency.iconDescription2') },
    { icon: faEnvelopeCircleCheck, id: 'icon3', title: t('agency.iconTitle3'), description: t('agency.iconDescription3') }
  ];

  const clearAgencyStorage = (): void => {
    localStorage.removeItem(Storage.AGENCY_CITY);
    localStorage.removeItem(Storage.AGENCY_END_DATE);
    localStorage.removeItem(Storage.AGENCY_NUM_CHILDREN);
    localStorage.removeItem(Storage.AGENCY_START_DATE);
    localStorage.removeItem(Storage.AGENCY_STATE);
    localStorage.removeItem(Storage.AGENCY_STREET);
    localStorage.removeItem(Storage.AGENCY_ZIP);
  };

  const continueToBook = (): void => {
    cookies.set(Storage.BOOKING_ORIGIN, '/family/dashboard', {
      path: '/',
    });

    const agencyCity = localStorage.getItem(Storage.AGENCY_CITY);
    const agencyEndDate = localStorage.getItem(Storage.AGENCY_END_DATE);
    const agencyNumberOfChildren = localStorage.getItem(Storage.AGENCY_NUM_CHILDREN);
    const agencyStartDate = localStorage.getItem(Storage.AGENCY_START_DATE);
    const agencyState = localStorage.getItem(Storage.AGENCY_STATE);
    const agencyStreet = localStorage.getItem(Storage.AGENCY_STREET);
    const agencyZipCode = localStorage.getItem(Storage.AGENCY_ZIP);

    const search = new URLSearchParams();
    if (agencyCity) search.append(Storage.AGENCY_CITY, agencyCity);
    if (agencyEndDate) search.append(Storage.AGENCY_END_DATE, agencyEndDate);
    if (agencyNumberOfChildren) search.append(Storage.AGENCY_NUM_CHILDREN, agencyNumberOfChildren);
    if (agencyStartDate) search.append(Storage.AGENCY_START_DATE, agencyStartDate);
    if (agencyState) search.append(Storage.AGENCY_STATE, agencyState);
    if (agencyStreet) search.append(Storage.AGENCY_STREET, agencyStreet);
    if (agencyZipCode) search.append(Storage.AGENCY_ZIP, agencyZipCode);

    clearAgencyStorage();
    navigate({ pathname: generatePath(Routes.parent.bookings.agencyDate), search: search.toString() });
  };

  const openIdentityCheckModal = (): void => {
    modal?.setModalName(ModalName.IdentityVerification);
  };

  const handleBack = (): void => {
    clearAgencyStorage();
    navigate(-1);
  };

  const handleBook = (): void => verification.checkVerification(continueToBook, openIdentityCheckModal);

  return (
    <div className="container mx-auto px-4 md:px-6 font-['Proxima Nova']">
      <button
        type='button'
        className='flex items-center mt-12 mb-6 text-gray-500 bg-transparent border-none cursor-pointer'
        onClick={handleBack}
        aria-label={t('common.back')}
      >
        <FontAwesomeIcon icon={faArrowLeft} className='w-6 h-7' />
        <span className='ml-4 text-base md:text-lg'>{t('common.back')}</span>
      </button>

      <div className='md:flex md:justify-between md:items-start'>
        <div className='md:w-1/2 mb-8 md:mb-0'>
          <h1 className='text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4'>
            {t('agency.title')} <span className='text-tansy'>{t('agency.agencyCare')}</span> {t('agency.titleEnd')}
          </h1>
          <div className='md:hidden mb-6'>
            <img
              src={AgencyCare}
              alt='Caregiver and child'
              className='w-full h-auto object-cover'
            />
          </div>
          <p className='text-lg md:text-xl lg:text-2xl text-gray-700 mb-6'>
            {t('agency.headline')}
          </p>
          <Button
            onClick={handleBook}
            buttonType={ButtonType.primary}
            buttonSize={ButtonSize.medium}
            className='inline-block text-white rounded-full py-3 px-14 hover:no-underline hover:text-white hover:opacity-70 text-lg font-semibold w-full md:w-auto'
          >
            {t('common.book')}
          </Button>
        </div>
        <div className='hidden md:flex md:w-1/2 justify-end'>
          <div className='w-full max-w-[449px] -mt-12'>
            <img
              src={AgencyCare}
              alt='Caregiver and child'
              className='w-full h-auto object-cover'
            />
          </div>
        </div>
      </div>

      <div className='md:my-12'>
        <h2 className='hidden md:block text-3xl md:text-4xl lg:text-5xl font-semibold text-center my-12'>{t('agency.how')}</h2>
        <div className='md:flex md:flex-row justify-between items-start gap-4 md:gap-2'>
          {/* Mobile-only container */}
          <div className='flex flex-col md:hidden border-2 border-emerald rounded-lg p-4'>
            <h2 className='text-3xl font-semibold text-center mb-8'>{t('agency.how')}</h2>
            {steps.map((step, index) => (
              <div key={step.id} className='flex flex-col items-center w-full'>
                <div className='flex-1 mb-8 max-w-xs flex flex-col items-center'>
                  <div className='relative inline-block mb-4'>
                    <div className='w-28 h-28 rounded-full absolute -left-6 -top-2' style={circleStyle} />
                    <div className='relative z-10'>
                      <FontAwesomeIcon
                        icon={step.icon}
                        className='w-32 h-32'
                        style={iconStyle}
                      />
                    </div>
                  </div>
                  <h3 className='text-xl font-bold mb-2 text-center'>{step.title}</h3>
                  <p className='text-gray-600 text-sm text-center'>{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className='flex justify-center w-full my-4'>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className='text-gray-400 w-6 h-6'
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          {steps.map((step, index) => (
            <div key={step.title} className='hidden md:flex flex-row items-start w-full'>
              <div className='flex-1 mb-8 md:mb-0 max-w-xs flex flex-col items-center'>
                <div className='relative inline-block mb-4'>
                  <div className='w-28 h-28 rounded-full absolute -left-6 -top-2' style={circleStyle} />
                  <div className='relative z-10'>
                    <FontAwesomeIcon
                      icon={step.icon}
                      className='w-32 h-32'
                      style={iconStyle}
                    />
                  </div>
                </div>
                <h3 className='text-xl font-bold mb-2 text-center'>{step.title}</h3>
                <p className='text-gray-600 text-sm text-center'>{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className='flex items-start justify-center md:w-16 pt-48'>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className='text-gray-400 w-6 h-6'
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className='text-center my-12'>
        <h3 className='text-2xl md:text-3xl font-semibold mb-4'>{t('agency.ready')}</h3>
        <Button
          onClick={handleBook}
          buttonType={ButtonType.primary}
          buttonSize={ButtonSize.medium}
          className='inline-block text-white rounded-full py-3 px-14 hover:no-underline hover:text-white hover:opacity-70 text-lg font-semibold w-full md:w-auto'
        >
          {t('common.book')}
        </Button>
      </div>

      <div className='w-1/3 mx-auto border-t-2 border-emerald my-12' />

      <div className='text-center mb-12'>
        <h3 className='text-2xl md:text-3xl lg:text-4xl font-semibold mb-4'>{t('agency.questions')}</h3>
        <p className='text-base md:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto'>
          {t('agency.contact')} <a href='mailto:agencycare@urbansitter.com' className='text-blue-600'>{t('agency.email')}</a>.
        </p>
      </div>
    </div>
  );
};
